import EmailIcon from '@mui/icons-material/Email';
import MapIcon from '@mui/icons-material/Map';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import LoadingButton from '@mui/lab/LoadingButton';
import {Box, CircularProgress, Stack, Tooltip, Typography} from '@mui/material';
import {get, isArray} from 'lodash';
import {FC, ReactNode, useContext, useMemo, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {useRecoilValue} from 'recoil';

import ControlledInput from '../../../../ui/controlled-input/ControlledInput';
import ManagerBusinessCard from '../../../../ui/manager-business-card/ManagerBusinessCard';
import ShowCompany from '../../../Companies/CompaniesTable/ShowCompany/ShowCompany';
import {MangerListModal, ShowManagerList} from '../../manager';
import StatusHistory from './StatusHistory';

import {ServiceRequestService} from '../../../../api/services/serviceRequest.service';
import {EventBusContext} from '../../../../providers/EventBus';
import {checkAuthorities} from '../../../../shared/helpers/checkAuthorities';
import {getFullName} from '../../../../shared/helpers/getFullName';
import {ICompany} from '../../../../shared/types/company.types';
import {IDeviceTypesDict} from '../../../../shared/types/deviceTypesDict.types';
import {IServiceRequest} from '../../../../shared/types/serviceRequest.types';
import {authState} from '../../../../store/auth/atoms';
import {EDIT_ADDRESS_ROLES, EDIT_WORKERS_ROLES, EDIT_WORKERS_STATUSES, STYLES} from './constants';
import {EditAddressFormModal, EditAddressFormModalHandlers} from './EditAddressFormModal';
import {getMapUrlForCoordinate, getMapUrlForText} from './helpers';
import {TypeAccountRoles} from '../../../../shared/types/user-account.types';
import {BASE_TITLES} from '../../../../shared/constants';
import {EditButton} from '../../../ui/EditButton';
import {EditWorkersFormModal, EditWorkersFormModalHandlers} from './EditWorkersFormModal/EditWorkersFormModal';
import {getIsUserCustomer, getIsUserWorker} from '../../../../global/lib';
import {formatIsoDate} from '../utils';
import {EditStartDateFormModal, EditStartDateFormModalHandlers} from './EditStartDateFormModal';
import {EditPlannedDateFormModal, EditPlannedDateFormModalHandlers} from './EditPlannedDateFormModal';
import {EditCustomerFormModal, EditCustomerFormModalHandlers} from './EditCustomerFormModal';

interface IGeneralProps {
  device: IDeviceTypesDict;
  serviceRequest: IServiceRequest;
  company: ICompany | null;
}

interface FormValues {
  notificationDate: null;
  installationDate: null;
  notification: string;
  worker: {label: string} | null;
  description: string;
}

const General: FC<IGeneralProps> = ({serviceRequest, company}) => {
  const {address} = serviceRequest;
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const {bus} = useContext(EventBusContext);
  const {user} = useRecoilValue(authState);
  const editAddressModalRef = useRef<EditAddressFormModalHandlers>(null);
  const editCustomerModalRef = useRef<EditCustomerFormModalHandlers>(null);
  const editWorkersModalRef = useRef<EditWorkersFormModalHandlers>(null);
  const editStartDateModalRef = useRef<EditStartDateFormModalHandlers>(null);
  const editPlannedDateModalRef = useRef<EditPlannedDateFormModalHandlers>(null);

  const {
    getValues,
    control,
    trigger,
    formState: {dirtyFields},
  } = useForm<FormValues>({
    defaultValues: {
      notificationDate: null,
      installationDate: null,
      notification: 'WITHOUT_NOTIFICATION',
      worker: null,
      description: serviceRequest.description ?? '',
    },
    mode: 'all',
  });

  const isCustomer = getIsUserCustomer(user);
  const isCustomerAuthor =
    !!serviceRequest.createdBy && !!user && [user.uid, user.userLogin, user.email].includes(serviceRequest.createdBy);
  const canCustomerEditRequest = isCustomer && isCustomerAuthor && serviceRequest.statusCode === 'CREATED';
  const isDirector = checkAuthorities(
    [
      TypeAccountRoles.MANAGER,
      TypeAccountRoles.PROJECT_MANAGER,
      TypeAccountRoles.FUNCTIONAL_ADMIN,
      TypeAccountRoles.ADMIN,
      TypeAccountRoles.OWNER,
      TypeAccountRoles.DIRECTOR,
    ],
    [...(user?.roles || [])],
  );
  const canEditAddress = checkAuthorities(EDIT_ADDRESS_ROLES, [...(user?.roles || [])]);
  const canEditGeneral = canEditAddress || canCustomerEditRequest;
  const canEditCustomer = canEditGeneral;
  const canEditWorkersByRole = checkAuthorities(EDIT_WORKERS_ROLES, [...(user?.roles || [])]);
  const canEditWorkersByStatus = EDIT_WORKERS_STATUSES.includes(serviceRequest?.statusCode);
  const workers = isArray(serviceRequest.workers) ? serviceRequest.workers.map(getFullName).join(', ') : null;
  const fullAddress = serviceRequest?.fullAddress || address?.fullAddress || '-';
  const mapAddress = address?.fiasAddress || fullAddress;
  const isWorker = useMemo(() => {
    return getIsUserWorker(user);
  }, [user]);

  const toggleCompanyModal = () => company && setShowCompanyModal(!showCompanyModal);

  const saveDescription = async () => {
    setIsSaving(true);
    try {
      await ServiceRequestService.setDescription(serviceRequest.id, getValues('description').trim());
      toast.success('Комментарий сохранен', {containerId: 'default'});
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditStartDateClick = () => {
    if (!canEditGeneral) return;
    editStartDateModalRef.current?.handleClickOpen();
  };

  const handleEditPlannedDateClick = () => {
    if (!canEditGeneral) return;
    editPlannedDateModalRef.current?.handleClickOpen();
  };

  const handleSaveDescription = () => {
    trigger().then((isValid) => {
      if (isValid) saveDescription();
    });
  };

  const handleEditAddressClick = () => {
    if (!canEditGeneral) return;
    editAddressModalRef.current?.handleClickOpen();
  };

  const handleEditWorkersClick = () => {
    if (!canEditWorkersByRole) return;
    editWorkersModalRef.current?.handleClickOpen();
  };

  const handleEditCustomerClick = () => {
    if (!canEditCustomer) return;
    editCustomerModalRef.current?.handleClickOpen();
  };

  const renderContactIcons = () => (
    <Stack direction="row" ml={0.5} spacing={0.5} alignItems="center">
      {serviceRequest.customerPhone && serviceRequest.customerPhone !== '-' && (
        <Tooltip
          PopperProps={{disablePortal: true}}
          title={
            <>
              <Typography component="div" variant="subtitle2">
                Позвонить
              </Typography>
              <Typography component="div" variant="caption">
                {serviceRequest.customerPhone}
              </Typography>
            </>
          }
        >
          <a href={`tel:${serviceRequest.customerPhone}`} style={{display: 'flex'}}>
            <PhoneIcon sx={{cursor: 'pointer'}} htmlColor="#2CC378" />
          </a>
        </Tooltip>
      )}

      {serviceRequest.email && serviceRequest.email !== '-' && (
        <Tooltip
          title={
            <>
              <Typography component="div" variant="subtitle2">
                Отправить эл. письмо
              </Typography>
              <Typography component="div" variant="caption">
                {serviceRequest.email}
              </Typography>
            </>
          }
        >
          <a href={`mailto:${serviceRequest.email}`} style={{display: 'flex'}}>
            <EmailIcon sx={{cursor: 'pointer'}} color="primary" />
          </a>
        </Tooltip>
      )}
    </Stack>
  );

  const renderInfoRow = (
    label: string,
    value: string | null | undefined,
    tooltipTitle?: string,
    additionalNode?: ReactNode,
  ) => {
    const displayValue = value || '-';
    return (
      <Stack direction="row" width="100%">
        <Typography sx={STYLES.label}>{label}</Typography>
        <Tooltip PopperProps={{disablePortal: true}} title={tooltipTitle || displayValue}>
          <Typography noWrap sx={STYLES.data}>
            {displayValue}
          </Typography>
        </Tooltip>
        {additionalNode && additionalNode}
      </Stack>
    );
  };

  const renderMapIcon = (href: string) => {
    return (
      <Tooltip
        title={
          <>
            <Typography component="div" variant="subtitle2">
              Карта
            </Typography>
            <Typography component="div" variant="caption">
              Посмотреть
            </Typography>
          </>
        }
      >
        <a target="_blank" href={href} rel="noreferrer" style={{display: 'flex'}}>
          <PlaceIcon color="error" />
        </a>
      </Tooltip>
    );
  };

  return (
    <Box>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" sx={STYLES.mainLayout}>
          <Stack direction="row" width="100%">
            <Stack direction="column" spacing={3} width="100%">
              <Typography sx={STYLES.companyName} onClick={toggleCompanyModal}>
                {company ? `Заказчик: ${company.fullCompanyName}` : <CircularProgress size="small" />}
              </Typography>

              <Stack direction="row" width="100%">
                <Stack direction="column" width="100%">
                  <Stack direction="row">
                    <Typography sx={STYLES.label}>Абонент</Typography>
                    <Tooltip PopperProps={{disablePortal: true}} title={serviceRequest?.customer || '-'}>
                      <Typography noWrap sx={STYLES.data}>
                        {serviceRequest?.customer || '-'}
                      </Typography>
                    </Tooltip>
                    {canEditCustomer && (
                      <Stack direction="row" ml={0.5} spacing={0.5} alignItems="center">
                        <EditButton title="Редактировать данные абонента" onClick={handleEditCustomerClick} />
                      </Stack>
                    )}
                    {renderContactIcons()}
                  </Stack>

                  {renderInfoRow('Лицевой счет', serviceRequest.extNumber)}

                  <Stack direction="row" alignItems="center">
                    <Typography sx={STYLES.label}>Адрес установки</Typography>
                    {address?.fiasAddress && (
                      <Tooltip title={address.fiasAddress}>
                        <MapIcon color="primary" style={{position: 'absolute', left: '167px'}} />
                      </Tooltip>
                    )}
                    <Tooltip PopperProps={{disablePortal: true}} title={fullAddress}>
                      <Typography noWrap sx={STYLES.data}>
                        {fullAddress}
                      </Typography>
                    </Tooltip>
                    <Stack direction="row" ml={0.5} spacing={0.5} alignItems="center">
                      {fullAddress !== '-' && renderMapIcon(getMapUrlForText(mapAddress))}
                      {canEditGeneral && <EditButton title="Редактировать адрес" onClick={handleEditAddressClick} />}
                    </Stack>
                  </Stack>

                  {renderInfoRow('Место установки', serviceRequest.taskLocation?.name)}

                  {serviceRequest.workerGroup &&
                    renderInfoRow(BASE_TITLES.workers.group, serviceRequest.workerGroup.name)}

                  {renderInfoRow(
                    BASE_TITLES.workers.plural,
                    workers,
                    undefined,
                    canEditWorkersByRole && canEditWorkersByStatus ? (
                      <Stack direction="row" ml={0.5} spacing={0.5} alignItems="center">
                        <EditButton title={BASE_TITLES.workers.editPlural} onClick={handleEditWorkersClick} />
                      </Stack>
                    ) : null,
                  )}

                  {(serviceRequest?.postalCode || address?.postalCode) &&
                    renderInfoRow('Почтовый индекс', serviceRequest?.postalCode || address?.postalCode)}

                  {address?.cadastre && renderInfoRow('Кадастровый номер', address.cadastre)}

                  {address?.latitude && address?.longitude && (
                    <Stack direction="row" width="100%">
                      <Typography sx={STYLES.label}>GPS-координаты</Typography>
                      <Tooltip
                        PopperProps={{disablePortal: true}}
                        title={`д. ${address.longitude}, ш. ${address.latitude}`}
                      >
                        <Typography noWrap sx={STYLES.data}>
                          {`д. ${address.longitude}, ш. ${address.latitude}`}
                        </Typography>
                      </Tooltip>
                      <Stack direction="row" ml={0.5} spacing={0.5} alignItems="center">
                        {renderMapIcon(getMapUrlForCoordinate(address.longitude, address.latitude))}
                      </Stack>
                    </Stack>
                  )}

                  <Stack direction="row" width="100%">
                    <Typography sx={STYLES.label}>Дата ввода заявки</Typography>
                    <Tooltip
                      PopperProps={{disablePortal: true}}
                      title={formatIsoDate(get(serviceRequest, 'startDate', ''))}
                    >
                      <Typography noWrap sx={STYLES.data}>
                        {formatIsoDate(get(serviceRequest, 'startDate', ''))}
                      </Typography>
                    </Tooltip>
                    {canEditGeneral && (
                      <Stack direction="row" ml={0.5} spacing={0.5} alignItems="center">
                        <EditButton title="Редактировать дату ввода заявки" onClick={handleEditStartDateClick} />
                      </Stack>
                    )}
                  </Stack>

                  <Stack direction="row" width="100%">
                    <Typography sx={STYLES.label}>Дата окончания заявки</Typography>
                    <Tooltip
                      PopperProps={{disablePortal: true}}
                      title={formatIsoDate(get(serviceRequest, 'plannedDate', ''))}
                    >
                      <Typography noWrap sx={STYLES.data}>
                        {formatIsoDate(get(serviceRequest, 'plannedDate', ''))}
                      </Typography>
                    </Tooltip>
                    {canEditGeneral && (
                      <Stack direction="row" ml={0.5} spacing={0.5} alignItems="center">
                        <EditButton title="Редактировать дату окончания заявки" onClick={handleEditPlannedDateClick} />
                      </Stack>
                    )}
                  </Stack>

                  {serviceRequest.statusCode === 'REJECTED' &&
                    renderInfoRow('Причина отмены заявки', serviceRequest.reasonRejection?.name)}

                  {serviceRequest.statusCode === 'APPROVED' &&
                    renderInfoRow('Акт закрытия', serviceRequest.reasonStatusCodeChanged)}
                </Stack>
              </Stack>
            </Stack>
          </Stack>

          {!!serviceRequest.manager && (
            <Stack direction="row" justifyContent="flex-end">
              <ManagerBusinessCard manager={serviceRequest.manager} changeManager={isDirector && <ShowManagerList />} />
            </Stack>
          )}
        </Stack>

        <Stack direction="row" spacing={2} sx={STYLES.container}>
          <StatusHistory serviceRequestId={serviceRequest.id} />
        </Stack>

        <Stack direction="column" spacing={1.5}>
          <Stack direction="row">
            <ControlledInput
              control={control}
              rows={3}
              rules={{
                maxLength: {
                  value: 1024,
                  message: 'Не более 1024 символов',
                },
              }}
              disabled={(!canCustomerEditRequest && isCustomer) || isWorker}
              multiline
              name="description"
              sx={STYLES.descriptionInput}
              size="small"
              placeholder="Замечания / Комментарии"
            />
          </Stack>

          <Stack direction="row" justifyContent="flex-end">
            <LoadingButton
              onClick={handleSaveDescription}
              size="small"
              variant="contained"
              loading={isSaving}
              disabled={!dirtyFields.description || (!canCustomerEditRequest && isCustomer)}
            >
              Сохранить комментарий
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>

      {company && showCompanyModal && (
        <ShowCompany open={showCompanyModal} handleClose={toggleCompanyModal} company={company} />
      )}

      <MangerListModal />
      <EditAddressFormModal ref={editAddressModalRef} serviceRequest={serviceRequest} />
      <EditWorkersFormModal ref={editWorkersModalRef} serviceRequest={serviceRequest} />
      <EditStartDateFormModal ref={editStartDateModalRef} serviceRequest={serviceRequest} />
      <EditPlannedDateFormModal ref={editPlannedDateModalRef} serviceRequest={serviceRequest} />
      <EditCustomerFormModal ref={editCustomerModalRef} serviceRequest={serviceRequest} />
    </Box>
  );
};

export default General;
